// Curated listening deck. Mix of single sentences (A1) and short dialogues (A2-B1).
// Optional siddis variant for items where a known dialectal form exists
// (validated against Dana's Stavanger native usage and NB Uttale notes).

export interface EcouteLine {
  speaker?: string;
  bokmal: string;
  /** Stavanger urban dialect (siddis) re-rendering, if known. */
  siddis?: string;
  french: string;
}

export interface EcouteItem {
  id: string;
  cefr: string;
  title: string;
  /** Free-text setting in French. */
  scene: string;
  /** Optional comprehension questions, displayed after playback. */
  questions?: { q: string; a: string }[];
  lines: EcouteLine[];
}

export const ECOUTE_DECK: EcouteItem[] = [
  // --- A1.1 ---
  { id: 'e001', cefr: 'A1.1', title: 'Salutations', scene: 'Deux collègues se croisent dans le couloir.',
    lines: [
      { speaker: 'Anna', bokmal: 'Hei! Hvordan har du det?', siddis: 'Hei! Kordan har du det?', french: 'Salut ! Comment ça va ?' },
      { speaker: 'Per',  bokmal: 'Bra, takk. Og du?',                                     french: 'Bien, merci. Et toi ?' },
      { speaker: 'Anna', bokmal: 'Helt fint.',                                            french: 'Très bien.' },
    ] },
  { id: 'e002', cefr: 'A1.1', title: 'Se présenter', scene: 'Premier jour à un cours de norvégien.',
    lines: [
      { speaker: 'Maria', bokmal: 'Hei, jeg heter Maria. Jeg er fra Frankrike.', french: 'Bonjour, je m’appelle Maria. Je viens de France.' },
      { speaker: 'Lars',  bokmal: 'Hyggelig å møte deg. Jeg heter Lars.',         french: 'Enchanté. Je m’appelle Lars.' },
    ],
    questions: [{ q: 'D’où vient Maria ?', a: 'De France.' }] },

  // --- A1.2 ---
  { id: 'e010', cefr: 'A1.2', title: 'Au café', scene: 'Commande au comptoir d’un café à Stavanger.',
    lines: [
      { speaker: 'Barista', bokmal: 'Hva vil du ha?',                  siddis: 'Ka vil du ha?',            french: 'Que veux-tu ?' },
      { speaker: 'Client',  bokmal: 'En kaffe og en bolle, takk.',                                          french: 'Un café et une brioche, merci.' },
      { speaker: 'Barista', bokmal: 'Det blir femtifem kroner.',                                            french: 'Ça fait cinquante-cinq couronnes.' },
      { speaker: 'Client',  bokmal: 'Vær så god.',                                                          french: 'Voilà.' },
    ],
    questions: [{ q: 'Combien coûte la commande ?', a: '55 couronnes.' }] },
  { id: 'e011', cefr: 'A1.2', title: 'L’heure', scene: 'Quelqu’un demande l’heure dans la rue.',
    lines: [
      { speaker: 'A', bokmal: 'Unnskyld, vet du hva klokken er?',     siddis: 'Unnskyld, vet du ka klokkå er?', french: 'Pardon, sais-tu quelle heure il est ?' },
      { speaker: 'B', bokmal: 'Den er kvart over to.',                                                          french: 'Il est deux heures et quart.' },
      { speaker: 'A', bokmal: 'Tusen takk.',                                                                    french: 'Merci beaucoup.' },
    ] },

  // --- A1.3 ---
  { id: 'e020', cefr: 'A1.3', title: 'Au supermarché', scene: 'On cherche un produit chez Rema 1000.',
    lines: [
      { speaker: 'Client',  bokmal: 'Hvor finner jeg brød?', siddis: 'Kor finner eg brød?', french: 'Où est-ce que je trouve le pain ?' },
      { speaker: 'Ansatt',  bokmal: 'Det er bak, ved siden av melken.',                       french: 'C’est au fond, à côté du lait.' },
      { speaker: 'Client',  bokmal: 'Tusen takk!',                                            french: 'Merci beaucoup !' },
    ] },
  { id: 'e021', cefr: 'A1.3', title: 'Demander le chemin', scene: 'Touriste perdu près du Domkirken.',
    lines: [
      { speaker: 'Turist',   bokmal: 'Unnskyld, hvor er Domkirken?', siddis: 'Unnskyld, kor er Domkirken?', french: 'Pardon, où est la cathédrale ?' },
      { speaker: 'Lokal',    bokmal: 'Gå rett frem, så til høyre. Den ligger ved sjøen.', french: 'Va tout droit, puis à droite. Elle est près de la mer.' },
      { speaker: 'Turist',   bokmal: 'Er det langt?',                                          french: 'C’est loin ?' },
      { speaker: 'Lokal',    bokmal: 'Nei, fem minutter.',                                     french: 'Non, cinq minutes.' },
    ],
    questions: [{ q: 'Combien de temps faut-il marcher ?', a: 'Cinq minutes.' }] },

  // --- A1.4 ---
  { id: 'e030', cefr: 'A1.4', title: 'Rendez-vous médical', scene: 'Réception chez le médecin (legekontoret).',
    lines: [
      { speaker: 'Resepsjonist', bokmal: 'God morgen, hva kan jeg hjelpe med?',                french: 'Bonjour, en quoi puis-je vous aider ?' },
      { speaker: 'Pasient',      bokmal: 'Jeg har vondt i halsen og hodet.',                   french: 'J’ai mal à la gorge et à la tête.' },
      { speaker: 'Resepsjonist', bokmal: 'Da kan du komme i morgen klokken ti.',               french: 'Vous pouvez venir demain à dix heures.' },
      { speaker: 'Pasient',      bokmal: 'Det passer fint. Takk.',                              french: 'Ça me convient. Merci.' },
    ],
    questions: [{ q: 'Quand est le rendez-vous ?', a: 'Demain à 10h.' }] },

  // --- A2.1 ---
  { id: 'e040', cefr: 'A2.1', title: 'Météo du week-end', scene: 'Bulletin radio court.',
    lines: [
      { bokmal: 'God ettermiddag. I helgen kan vi forvente regn og vind på Vestlandet.', french: 'Bonjour. Ce week-end, nous pouvons nous attendre à de la pluie et du vent dans l’Ouest.' },
      { bokmal: 'Temperaturene blir mellom åtte og tolv grader.',                          french: 'Les températures seront entre 8 et 12 degrés.' },
      { bokmal: 'På søndag kommer solen tilbake.',                                          french: 'Dimanche, le soleil sera de retour.' },
    ],
    questions: [
      { q: 'Quel temps fera-t-il samedi ?', a: 'De la pluie et du vent.' },
      { q: 'Quelle température maximale ?', a: '12 degrés.' },
    ] },

  // --- A2.2 ---
  { id: 'e050', cefr: 'A2.2', title: 'Invitation à dîner', scene: 'Conversation téléphonique entre voisins.',
    lines: [
      { speaker: 'Kari',  bokmal: 'Hei, det er Kari. Har du lyst til å komme på middag på lørdag?', french: 'Salut, c’est Kari. Tu veux venir dîner samedi ?' },
      { speaker: 'Tom',   bokmal: 'Så hyggelig! Hva slags mat blir det?',                            french: 'Avec plaisir ! Qu’est-ce qu’on mange ?' },
      { speaker: 'Kari',  bokmal: 'Jeg lager fiskesuppe. Du spiser fisk, ikke sant?',                french: 'Je fais une soupe de poisson. Tu manges du poisson, non ?' },
      { speaker: 'Tom',   bokmal: 'Ja, det elsker jeg. Jeg tar med vin.',                            french: 'Oui, j’adore ça. J’apporte du vin.' },
      { speaker: 'Kari',  bokmal: 'Perfekt. Vi sees klokken syv.',                                   french: 'Parfait. À samedi 19 h.' },
    ],
    questions: [
      { q: 'Quel jour est l’invitation ?', a: 'Samedi.' },
      { q: 'Que va apporter Tom ?',         a: 'Du vin.' },
    ] },

  // --- A2.3 ---
  { id: 'e060', cefr: 'A2.3', title: 'Annonce en gare', scene: 'Quai de la gare centrale d’Oslo.',
    lines: [
      { bokmal: 'Toget til Bergen klokken seksten femten er forsinket med tjue minutter.', french: 'Le train pour Bergen de 16 h 15 a un retard de 20 minutes.' },
      { bokmal: 'Vi beklager forsinkelsen.',                                                 french: 'Nous nous excusons pour le retard.' },
      { bokmal: 'Reisende til Trondheim, vennligst gå til spor fire.',                       french: 'Voyageurs pour Trondheim, veuillez vous rendre voie 4.' },
    ],
    questions: [
      { q: 'Quel train est en retard ?',     a: 'Celui pour Bergen.' },
      { q: 'Où vont les passagers de Trondheim ?', a: 'Voie 4.' },
    ] },

  // --- A2.4 ---
  { id: 'e070', cefr: 'A2.4', title: 'Réunion de travail', scene: 'Court extrait d’une réunion d’équipe.',
    lines: [
      { speaker: 'Sjef',  bokmal: 'Vi må diskutere planen for neste kvartal.',                          french: 'Nous devons discuter du plan pour le prochain trimestre.' },
      { speaker: 'Ola',   bokmal: 'Jeg foreslår at vi fokuserer på markedsføring i sosiale medier.',    french: 'Je propose qu’on se concentre sur le marketing sur les réseaux sociaux.' },
      { speaker: 'Eva',   bokmal: 'Det høres bra ut, men vi trenger også flere ressurser til utvikling.', french: 'Ça paraît bien, mais il nous faut aussi plus de ressources pour le développement.' },
      { speaker: 'Sjef',  bokmal: 'Greit. La oss møtes igjen på fredag.',                                french: 'D’accord. Retrouvons-nous vendredi.' },
    ],
    questions: [
      { q: 'Que propose Ola ?', a: 'De se concentrer sur le marketing.' },
      { q: 'Quand est la prochaine réunion ?', a: 'Vendredi.' },
    ] },

  // --- B1.1 ---
  { id: 'e080', cefr: 'B1.1', title: 'Récit de voyage', scene: 'Une amie raconte ses vacances dans les Lofoten.',
    lines: [
      { bokmal: 'Forrige uke var jeg på Lofoten med mannen min.',                                 french: 'La semaine dernière, j’étais aux Lofoten avec mon mari.' },
      { bokmal: 'Vi bodde i en gammel rorbu rett ved sjøen.',                                     french: 'Nous logions dans une vieille cabane de pêcheur juste au bord de la mer.' },
      { bokmal: 'Hver morgen så vi solen stå opp over fjellene.',                                  french: 'Chaque matin, nous voyions le soleil se lever sur les montagnes.' },
      { bokmal: 'Det var den vakreste turen vi noen gang har vært på.',                            french: 'C’était le plus beau voyage que nous ayons jamais fait.' },
    ],
    questions: [
      { q: 'Où ont-ils logé ?',  a: 'Dans une vieille cabane (rorbu).' },
      { q: 'Avec qui était-elle ?', a: 'Avec son mari.' },
    ] },

  // --- B1.2 ---
  { id: 'e090', cefr: 'B1.2', title: 'Opinion sur le logement', scene: 'Discussion au déjeuner sur le prix de l’immobilier.',
    lines: [
      { speaker: 'Ida',   bokmal: 'Jeg synes det er helt urimelig hvor mye en leilighet koster i Stavanger nå.', french: 'Je trouve complètement déraisonnable combien coûte un appartement à Stavanger en ce moment.' },
      { speaker: 'Bjørn', bokmal: 'Helt enig. Prisene har gått opp tjue prosent på to år.',                       french: 'Tout à fait d’accord. Les prix ont monté de 20 % en deux ans.' },
      { speaker: 'Ida',   bokmal: 'Kommunen burde bygge flere rimelige boliger.',                                 french: 'La commune devrait construire plus de logements abordables.' },
      { speaker: 'Bjørn', bokmal: 'Ja, men det tar tid før noe endrer seg.',                                      french: 'Oui, mais ça prend du temps avant que les choses changent.' },
    ],
    questions: [
      { q: 'De combien les prix ont-ils augmenté ?', a: '20 % en deux ans.' },
      { q: 'Que devrait faire la commune ?', a: 'Construire plus de logements abordables.' },
    ] },
];
